import { Injectable, inject } from '@angular/core';

import { DeviceDetectionService } from './device-detection.service';

/**
 * Browser families that can be detected
 */
export type BrowserFamily =
  | 'Chrome'
  | 'Safari'
  | 'Firefox'
  | 'Edge'
  | 'Opera'
  | 'SamsungInternet'
  | 'Other';

/**
 * Operating system families that can be detected
 */
export type OsFamily =
  | 'iOS'
  | 'Android'
  | 'Windows'
  | 'macOS'
  | 'Linux'
  | 'Other';

/**
 * Pointer input types
 */
export type PointerType = 'coarse' | 'fine' | 'unknown';

// Browser detection regexes (order matters - Edge before Chrome, Opera before Chrome, etc.)
const EDGE_USER_AGENT_REGEX = /edg\//i;
const EDGE_IOS_USER_AGENT_REGEX = /edgios/i;
const OPERA_USER_AGENT_REGEX = /opr\//i;
const LEGACY_OPERA_USER_AGENT_REGEX = /opera/i;
const SAMSUNG_INTERNET_USER_AGENT_REGEX = /samsungbrowser\//i;
const CHROME_USER_AGENT_REGEX = /chrome\//i;
const CHROME_IOS_USER_AGENT_REGEX = /crios/i;
const FIREFOX_USER_AGENT_REGEX = /firefox\//i;
const FIREFOX_IOS_USER_AGENT_REGEX = /fxios/i;
const SAFARI_USER_AGENT_REGEX = /safari\//i;

// OS detection regexes
const IOS_USER_AGENT_REGEX = /iphone|ipad|ipod/i;
const ANDROID_USER_AGENT_REGEX = /android/i;
const WINDOWS_USER_AGENT_REGEX = /windows/i;
const MACOS_USER_AGENT_REGEX = /macintosh|mac os x/i;
const LINUX_USER_AGENT_REGEX = /linux/i;
const DEVICE_CLASSIFICATION_SOURCE = 'ua-touch-pointer-heuristic';

/**
 * Service for collecting device context fields for metrics
 * Provides viewport size, screen size, pointer type, hover support, camera availability, browser and OS detection
 */
@Injectable({ providedIn: 'root' })
export class MetricDeviceContextService {
  private readonly deviceDetection = inject(DeviceDetectionService);

  /**
   * Collects all device context fields for metric logging
   * Safe for SSR - returns 'unknown' when browser APIs are unavailable
   */
  getDeviceContextFields(): Record<string, string> {
    return {
      DeviceType: this.deviceDetection.isMobileOrTablet()
        ? 'MobileOrTablet'
        : 'Desktop',
      DeviceClassificationSource: DEVICE_CLASSIFICATION_SOURCE,
      ViewportWidth: this.serializeNumber(globalThis.innerWidth),
      ViewportHeight: this.serializeNumber(globalThis.innerHeight),
      ScreenWidth: this.serializeNumber(globalThis.screen?.width),
      ScreenHeight: this.serializeNumber(globalThis.screen?.height),
      PointerType: this.getPointerType(),
      HoverSupported: String(this.matchesMedia('(hover: hover)')),
      CameraApiAvailable: String(this.isCameraApiAvailable()),
      BrowserFamily: this.getBrowserFamily(),
      OsFamily: this.getOsFamily(),
    };
  }

  /**
   * Detects pointer input type using media queries
   */
  private getPointerType(): PointerType {
    if (this.matchesMedia('(pointer: coarse)')) return 'coarse';
    if (this.matchesMedia('(pointer: fine)')) return 'fine';
    return 'unknown';
  }

  /**
   * Checks if camera API is available
   */
  private isCameraApiAvailable(): boolean {
    return (
      typeof globalThis.navigator?.mediaDevices?.getUserMedia === 'function'
    );
  }

  /**
   * Detects browser family from user agent
   * Order matters - check Edge before Chrome, Opera before Chrome, etc.
   */
  private getBrowserFamily(): BrowserFamily {
    const userAgent = globalThis.navigator?.userAgent ?? '';

    if (
      EDGE_USER_AGENT_REGEX.test(userAgent) ||
      EDGE_IOS_USER_AGENT_REGEX.test(userAgent)
    )
      return 'Edge';
    if (
      OPERA_USER_AGENT_REGEX.test(userAgent) ||
      LEGACY_OPERA_USER_AGENT_REGEX.test(userAgent)
    )
      return 'Opera';
    if (SAMSUNG_INTERNET_USER_AGENT_REGEX.test(userAgent))
      return 'SamsungInternet';
    if (
      CHROME_USER_AGENT_REGEX.test(userAgent) ||
      CHROME_IOS_USER_AGENT_REGEX.test(userAgent)
    )
      return 'Chrome';
    if (
      FIREFOX_USER_AGENT_REGEX.test(userAgent) ||
      FIREFOX_IOS_USER_AGENT_REGEX.test(userAgent)
    )
      return 'Firefox';
    if (SAFARI_USER_AGENT_REGEX.test(userAgent)) return 'Safari';
    return 'Other';
  }

  /**
   * Detects OS family from user agent
   */
  private getOsFamily(): OsFamily {
    const userAgent = globalThis.navigator?.userAgent ?? '';

    if (IOS_USER_AGENT_REGEX.test(userAgent)) return 'iOS';
    if (ANDROID_USER_AGENT_REGEX.test(userAgent)) return 'Android';
    if (WINDOWS_USER_AGENT_REGEX.test(userAgent)) return 'Windows';
    if (MACOS_USER_AGENT_REGEX.test(userAgent)) return 'macOS';
    if (LINUX_USER_AGENT_REGEX.test(userAgent)) return 'Linux';
    return 'Other';
  }

  /**
   * Safely checks media query match
   * Returns false if matchMedia is not available (SSR)
   */
  private matchesMedia(query: string): boolean {
    if (typeof globalThis.matchMedia !== 'function') return false;
    return globalThis.matchMedia(query).matches;
  }

  /**
   * Serializes number to string, returns 'unknown' for invalid values
   */
  private serializeNumber(value: number | undefined): string {
    return Number.isFinite(value) ? String(value) : 'unknown';
  }
}
